import React from 'react';
import { CardElement } from '@stripe/react-stripe-js';
import { StripeFormProvider, useForm } from './StateManagement';
import FormInput from '../FormInput';
import { StripeSDKLoader } from '../../utils/StripeSdkLoader';
import { getCoupon } from '../../utils/requests';

const CARD_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      color: '#1e293b',
      fontSize: '16px',
      fontFamily: '"Rubik", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSmoothing: 'antialiased',
      '::placeholder': {
        color: '#94a3b8',
      },
    },
    invalid: {
      color: '#ef4444',
      ':focus': {
        color: '#1e293b',
      },
    },
  },
};

const FormFields = () => {
  const { state, updateModel } = useForm();
  const { model, loading, errors } = state;

  return (
    <>
      <div className="space-y-5">
        <div>
          <FormInput
            required
            errors={errors['email'] || []}
            model={model}
            setValue={(e) => updateModel('email', e)}
            label="Email address"
            modelName="signup_form"
            value={model.email}
            attribute="email"
            type="email"
            className="signup-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormInput
              required
              errors={errors['password'] || []}
              model={model}
              label="Password"
              modelName="signup_form"
              attribute="password"
              type="password"
              className="signup-input"
            />
          </div>

          <div>
            <FormInput
              required
              errors={errors['password_confirmation'] || []}
              model={model}
              label="Confirm password"
              modelName="signup_form"
              attribute="password_confirmation"
              type="password"
              className="signup-input"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200">
          <h4 className="text-sm font-semibold text-slate-700 mb-4">Organization details</h4>
          <FormInput
            required
            errors={errors['name'] || []}
            model={model}
            setValue={(e) => updateModel('name', e)}
            value={model.name}
            label="Organization name"
            modelName="signup_form"
            attribute="name"
            type="text"
            placeholder="e.g., IUEC Local 2"
            className="signup-input"
          />
        </div>

        <div className="pt-4 border-t border-slate-200">
          <h4 className="text-sm font-semibold text-slate-700 mb-4">Payment information</h4>

          <div className="mb-4 grid grid-cols-2 gap-4">
            <FormInput
              required
              errors={errors['first_name'] || []}
              model={model}
              setValue={(e) => updateModel('first_name', e)}
              value={model.first_name}
              label="First name"
              modelName="signup_form"
              attribute="first_name"
              type="text"
              placeholder="First name"
              className="signup-input"
            />
            <FormInput
              required
              errors={errors['last_name'] || []}
              model={model}
              setValue={(e) => updateModel('last_name', e)}
              value={model.last_name}
              label="Last name"
              modelName="signup_form"
              attribute="last_name"
              type="text"
              placeholder="Last name"
              className="signup-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Card details <span className="text-red-500">*</span>
            </label>
            <div className="p-4 border border-slate-200 rounded-lg bg-slate-50 focus-within:ring-2 focus-within:ring-amber-500 focus-within:border-amber-500 transition-all">
              <CardElement
                options={{ ...CARD_OPTIONS, disabled: loading }}
                disabled={loading}
              />
            </div>
            {!!errors.card &&
              errors.card.map((e, i) => (
                <p key={i} className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  {e.message}
                </p>
              ))}
          </div>
        </div>

        <div className="pt-2">
          <CouponField />
        </div>
      </div>
    </>
  );
};

const CouponField = (props) => {
  const { state, dispatch, updateModel } = useForm();
  const { model, couponLoading, errors } = state;

  const handleCoupon = async (e) => {
    e.preventDefault();

    dispatch({ type: 'TOGGLE_COUPON_LOADING' });
    try {
      const { data } = await getCoupon({ coupon: model.coupon });

      if (data.error || data.coupon.invalid) {
        dispatch({
          type: 'SET_ERRORS',
          errors: { ...errors, coupon: ['Invalid coupon code'] },
        });
        console.error(data.error);
      } else {
        dispatch({
          type: 'SET_APPLIED_COUPON',
          appliedCoupon: data.coupon,
        });
        updateModel('coupon_valid', true);

        dispatch({
          type: 'SET_ERRORS',
          errors: { ...errors, coupon: [] },
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      dispatch({ type: 'TOGGLE_COUPON_LOADING' });
    }
  };

  const coupondDisabled =
    !model.coupon || model.coupon.trim().length < 1 || couponLoading;

  return (
    <div className="flex items-end gap-3">
      <div className="flex-1">
        <FormInput
          errors={errors['coupon'] || []}
          model={model}
          setValue={(e) => updateModel('coupon', e)}
          value={model.coupon}
          label="Coupon code (optional)"
          modelName="signup_form"
          attribute="coupon"
          type="text"
          placeholder="Enter code"
          className="signup-input"
        />

        <input
          defaultValue={model.coupon_valid}
          name="signup_form[coupon_valid]"
          type="hidden"
        />
      </div>

      <button
        disabled={coupondDisabled}
        onClick={handleCoupon}
        className={`px-4 py-2.5 text-sm font-medium rounded-lg border transition-all duration-150 ${
          coupondDisabled
            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
            : 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800 active:scale-[0.98]'
        }`}
      >
        {couponLoading ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          'Apply'
        )}
      </button>
    </div>
  );
};

const Form = () => {
  const { state, dispatch, stripe, elements } = useForm();
  const { model, loading, paymentMethod, error, appliedCoupon } = state;

  const formRef = React.useRef(null);

  const handleSignup = async (e) => {
    e.preventDefault();

    dispatch({ type: 'TOGGLE_LOADING' });
    if (!stripe || !elements) {
      dispatch({ type: 'TOGGLE_LOADING' });
      return;
    }

    const fullName = [model.first_name, model.last_name].filter(Boolean).join(' ') || '';
    const result = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement),
      billing_details: {
        name: fullName,
        email: model.email,
      },
    });

    if (!!result.error) {
      dispatch({ type: 'TOGGLE_LOADING' });
      dispatch({ type: 'SET_ERROR', error: result.error.message });
    } else {
      dispatch({
        type: 'SET_PAYMENT_METHOD',
        paymentMethod: result.paymentMethod.id,
      });

      const form = formRef.current;
      const hiddenInput = document.createElement('input');
      hiddenInput.setAttribute('type', 'hidden');
      hiddenInput.setAttribute('name', 'signup_form[payment_method]');
      hiddenInput.setAttribute('value', result.paymentMethod.id);
      form.appendChild(hiddenInput);

      // Re-enable fieldset before submit - disabled fields don't submit their values
      const fieldset = form.querySelector('fieldset');
      if (fieldset) fieldset.disabled = false;
      form.submit();
    }
  };

  const token = document.querySelector('[name=csrf-token]').content;

  return (
    <div className="signup-form">
      <form
        ref={formRef}
        action="/signup"
        method="post"
        onSubmit={handleSignup}
      >
        <input type="hidden" name="authenticity_token" defaultValue={token} />
        <input type="hidden" name="signup_form[price]" defaultValue={model.price} />
        <input
          type="hidden"
          name="signup_form[platform]"
          value={model.platform}
        />
        <input
          id="signup_form_payment_method"
          type="hidden"
          name="signup_form[payment_method]"
          defaultValue={paymentMethod}
        />

        <fieldset disabled={loading}>
          <FormFields />
        </fieldset>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {!!appliedCoupon.valid && (
          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
            <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span className="text-sm text-emerald-700 font-medium">Coupon "{appliedCoupon.id}" applied!</span>
          </div>
        )}

        <div className="mt-6">
          <button
            disabled={loading}
            type="submit"
            className={`w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl text-base font-semibold transition-all duration-150 ${
              loading
                ? 'bg-amber-400 text-slate-900 cursor-wait'
                : 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:-translate-y-0.5 active:translate-y-0'
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>Start your subscription</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                </svg>
              </>
            )}
          </button>
        </div>

        <p className="mt-4 text-xs text-center text-slate-500">
          By subscribing, you agree to our{' '}
          <a href="/terms-of-use" className="text-slate-700 hover:text-amber-600 underline">Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy-policy" className="text-slate-700 hover:text-amber-600 underline">Privacy Policy</a>
        </p>
      </form>
    </div>
  );
};

const SignupForm = ({
  stripe_publishable_key = '',
  model = {},
  errors = {},
}) => {
  return (
    <StripeSDKLoader stripe_publishable_key={stripe_publishable_key}>
      <StripeFormProvider model={model} errors={errors}>
        <Form />
      </StripeFormProvider>
    </StripeSDKLoader>
  );
};

export default SignupForm;
